import {
  Button,
  Flex,
  Heading,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from '@chakra-ui/react';
import { VariantBranchSupplier } from '@texas/api/endpoints/variantsApi';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { FeatureGuard } from '@texas/components/shared/featureGuard/FeatureGuard';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { UserAvatar } from '@texas/components/shared/users/UserAvatar';
import { TagWithTooltip } from '@texas/components/shared/TagWithTooltip';
import { useClientConfiguration } from '@texas/hooks/useClientConfiguration';
import { useTranslation } from 'react-i18next';

export interface MirrorProps {
  type: 'main' | 'follower';
  value: string;
}

export function SupplierCell({
  supplier,
  articleId,
  mirrorValue,
  enableMirror,
  isProductDevelopment,
  onDisable,
  onEnable,
  onMirrorClick,
  onChooseContacts,
  onCreatePurchaseInfo,
  onEditPurchaseInfo,
  mirrorModeOnClick,
}: {
  supplier: VariantBranchSupplier;
  articleId: number;
  mirrorValue: MirrorProps | null;
  enableMirror: boolean;
  isProductDevelopment: boolean;
  onDisable: (supplierId: number, branchId: number) => void;
  onEnable: (supplierId: number, branchId: number) => void;
  onMirrorClick: () => void;
  onChooseContacts: (supplierId: number) => void;
  onCreatePurchaseInfo: () => void;
  onEditPurchaseInfo: () => void;
  mirrorModeOnClick?: () => void;
}) {
  const { t } = useTranslation();
  const { isProduction } = useClientConfiguration();
  const pendingProductApprovals = supplier.pendingProductApprovalCount ?? 0;
  const acceptedProductApprovals = supplier.acceptedProductApprovalCount ?? 0;
  const pendingInquiries = supplier.pendingInquiryCount ?? 0;
  const acceptedInquiries = supplier.acceptedInquiryCount ?? 0;
  const visibleContacts = supplier.contacts.slice(0, 2);
  const additionalContactCount =
    supplier.contacts.length - visibleContacts.length;

  return (
    <Flex
      role="group"
      direction="column"
      w="full"
      minH={{ base: '148px', md: '220px' }}
      p={4}
      gap={3}
      align="start"
      overflow="hidden"
      bg="entityDirectory.surface"
      border="1px solid"
      borderColor="entityDirectory.border"
      opacity={supplier.disabled ? 0.55 : 1}
      onClick={mirrorModeOnClick}
    >
      <Flex direction="column" gap={1} w="full">
        <Flex align="center" gap={2} wrap="wrap">
          <Heading
            fontSize="sm"
            noOfLines={1}
            variant={supplier.disabled ? 'sub' : undefined}
            textDecoration={supplier.disabled ? 'line-through' : undefined}
          >
            {supplier.supplierName}
          </Heading>
          {supplier.isMain && (
            <Tag borderRadius={0} colorScheme="green" size="sm">
              <TagLabel fontSize="2xs" fontWeight="bold">
                {t('general.main')}
              </TagLabel>
            </Tag>
          )}
        </Flex>
        {!!supplier.tags?.length && (
          <Flex gap={1} wrap="wrap" w="full">
            {supplier.tags.map((tag) => (
              <TagWithTooltip key={tag.id} tag={tag} />
            ))}
          </Flex>
        )}
        {(supplier.contacts.length === 0 ||
          (supplier.currencyCode && isProductDevelopment)) && (
          <Flex
            align="center"
            gap={1}
            wrap="wrap"
            color="entityDirectory.mutedText"
            fontSize="xs"
          >
            {supplier.contacts.length === 0 && (
              <Text noOfLines={1}>{t('variant.supplier.noContacts')}</Text>
            )}
            {supplier.contacts.length === 0 &&
              supplier.currencyCode &&
              isProductDevelopment && <Text>·</Text>}
            {supplier.currencyCode && isProductDevelopment && (
              <Text noOfLines={1}>
                {t('variant.supplier.purchaseTerms', {
                  currency: supplier.currencyCode,
                })}
              </Text>
            )}
          </Flex>
        )}
        {supplier.contacts.length > 0 && (
          <Flex align="center" gap={2} mt={1} wrap="wrap">
            {visibleContacts.map((contact) => (
              <UserAvatar
                key={contact.id}
                name={contact.name}
                email={contact.email}
                label={contact.email}
                showTeams={false}
                size="sm"
              />
            ))}
            {additionalContactCount > 0 && (
              <Text
                color="entityDirectory.mutedText"
                fontSize="xs"
                fontWeight="semibold"
              >
                +{additionalContactCount}
              </Text>
            )}
          </Flex>
        )}
      </Flex>

      {isProductDevelopment && (
        <Flex w="full" mt="auto" flexShrink={0}>
          <SupplierMetric
            label={t('variant.supplier.productApprovals')}
            pending={pendingProductApprovals}
            accepted={acceptedProductApprovals}
            to="article.productApproval"
            navobj={{
              articleId,
              branchId: supplier.branchId,
              supplierId: supplier.supplierId,
            }}
          />
          {!isProduction && (
            <FeatureGuard hideBadge={true}>
              <SupplierMetric
                label={t('variant.supplier.inquiries')}
                pending={pendingInquiries}
                accepted={acceptedInquiries}
                to="variant.inquiry"
                navobj={{
                  articleId,
                  variantId: supplier.variantId,
                  branchId: supplier.branchId,
                  supplierId: supplier.supplierId,
                  status: 'all',
                }}
              />
            </FeatureGuard>
          )}
          <SupplierMetric
            label={t('navigation.versions')}
            count={supplier.versionCount ?? 0}
            to="variant.versions"
            navobj={{
              articleId,
              variantId: supplier.variantId,
              branchId: supplier.branchId,
              supplierId: supplier.supplierId,
            }}
          />
        </Flex>
      )}

      {!supplier.currencyCode && isProductDevelopment && (
        <Button
          p={0}
          size="xs"
          variant="texas-link"
          onClick={(e) => {
            e.stopPropagation();
            onCreatePurchaseInfo();
          }}
        >
          {t('variant.supplier.addPurchaseInfo')}
        </Button>
      )}

      {mirrorValue && (
        <Tag
          color="white"
          bg={mirrorValue.type === 'main' ? 'texas.sand.100' : 'gray.500'}
          _light={{
            bg:
              mirrorValue.type === 'main'
                ? 'texas.burntSienna.100'
                : 'gray.200',
          }}
        >
          <TagLeftIcon boxSize={defaultIconSize} as={Icons.Link} />
          <TagLabel fontWeight="bold">{mirrorValue.value}</TagLabel>
        </Tag>
      )}
      {mirrorModeOnClick === undefined && (
        <>
          <Flex columnGap={2} flexWrap="wrap">
            {supplier.currencyCode && (
              <FeatureGuard hideBadge={true} optOut={!isProductDevelopment}>
                <Button
                  p={0}
                  size="xs"
                  variant="texas-link"
                  visibility="hidden"
                  _groupHover={{ visibility: 'visible' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditPurchaseInfo();
                  }}
                >
                  {t('variant.supplier.editPurchaseInfo')}
                </Button>
              </FeatureGuard>
            )}
            {supplier.disabled ? (
              <Button
                p={0}
                size="xs"
                variant="texas-link"
                color="whiteAlpha.600"
                _light={{ color: 'blackAlpha.800' }}
                textDecoration="underline"
                visibility="hidden"
                _groupHover={{ visibility: 'visible' }}
                onClick={() => onEnable(supplier.supplierId, supplier.branchId)}
              >
                {t('general.enable')}
              </Button>
            ) : (
              <>
                {mirrorValue?.type !== 'main' && (
                  <Button
                    p={0}
                    size="xs"
                    variant="texas-link-danger"
                    visibility="hidden"
                    _groupHover={{ visibility: 'visible' }}
                    onClick={() =>
                      onDisable(supplier.supplierId, supplier.branchId)
                    }
                  >
                    {t('general.disable')}
                  </Button>
                )}

                <Button
                  p={0}
                  size="xs"
                  variant="texas-link"
                  visibility="hidden"
                  _groupHover={{ visibility: 'visible' }}
                  onClick={() => onChooseContacts(supplier.supplierId)}
                >
                  {t('variant.supplier.changeContacts')}
                </Button>
                {enableMirror && (
                  <>
                    {mirrorValue?.type !== 'main' && (
                      <Button
                        p={0}
                        size="xs"
                        variant="texas-link"
                        visibility="hidden"
                        _groupHover={{ visibility: 'visible' }}
                        onClick={onMirrorClick}
                      >
                        {mirrorValue?.type === 'follower'
                          ? t('variant.supplier.stopMirror')
                          : t('general.mirror')}
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
          </Flex>
        </>
      )}
    </Flex>
  );
}

function SupplierMetric({
  label,
  count,
  pending,
  accepted,
  to,
  navobj,
}: {
  label: string;
  count?: number;
  pending?: number;
  accepted?: number;
  to: string;
  navobj: Record<string, number | string>;
}) {
  const { t } = useTranslation();
  const pendingCount = pending ?? 0;
  const acceptedCount = accepted ?? 0;
  const total = count ?? pendingCount + acceptedCount;
  const showBreakdown = pending !== undefined && accepted !== undefined;

  return (
    <AngularNavLink
      to={to}
      navobj={navobj}
      display="flex"
      flexDirection="column"
      flex={1}
      minW={0}
      px={3}
      py={2}
      gap={0.5}
      textDecoration="none"
      _hover={{
        bg: 'whiteAlpha.100',
        textDecoration: 'none',
        _light: { bg: 'blackAlpha.100' },
      }}
    >
      <Text fontWeight="bold" lineHeight="shorter">
        {total}
      </Text>
      <Text
        color="entityDirectory.mutedText"
        fontSize="2xs"
        fontWeight="semibold"
        letterSpacing="wider"
        textTransform="uppercase"
      >
        {label}
      </Text>
      {showBreakdown && (pendingCount > 0 || acceptedCount > 0) && (
        <Text
          color={pendingCount > 0 ? 'orange.300' : 'entityDirectory.mutedText'}
          _light={{
            color:
              pendingCount > 0 ? 'orange.700' : 'entityDirectory.mutedText',
          }}
          fontSize="2xs"
        >
          {t('variant.supplier.pendingAccepted', {
            pending: pendingCount,
            accepted: acceptedCount,
          })}
        </Text>
      )}
    </AngularNavLink>
  );
}
